import { convertirAMXN, type Moneda } from '../dinero';
import type { MetodoPago } from '../estados';

export interface LineaVentaCalculable {
  cantidad: number;
  precioUnitario: number;
  total: number;
  descuento: number;
  ajusteCorreccion?: number;
}

export function netoLineaVenta(
  linea: Pick<LineaVentaCalculable, 'total' | 'descuento' | 'ajusteCorreccion'>,
): number {
  return linea.total - linea.descuento + (linea.ajusteCorreccion ?? 0);
}

export function totalesVenta(lineas: readonly LineaVentaCalculable[]) {
  const subtotal = lineas.reduce((s, l) => s + l.cantidad * l.precioUnitario, 0);
  const descuento = lineas.reduce((s, l) => s + l.descuento, 0);
  const ajusteCorreccion = lineas.reduce((s, l) => s + (l.ajusteCorreccion ?? 0), 0);
  return { subtotal, descuento, ajusteCorreccion, total: subtotal - descuento + ajusteCorreccion };
}

/**
 * Reparte un importe entre partes según su peso (p. ej. el precio de lista de cada servicio).
 * Cada parte se redondea y la última absorbe el resto, así la suma siempre da el importe.
 * Sin pesos positivos, se reparte en partes iguales.
 */
export function repartirImporte(importe: number, pesos: readonly number[]): number[] {
  if (pesos.length === 0) return [];
  const suma = pesos.reduce((s, p) => s + Math.max(0, p), 0);
  const base = suma > 0 ? pesos.map((p) => Math.max(0, p)) : pesos.map(() => 1);
  const totalBase = suma > 0 ? suma : pesos.length;
  const partes = base.slice(0, -1).map((p) => Math.round((importe * p) / totalBase));
  return [...partes, importe - partes.reduce((s, p) => s + p, 0)];
}

export interface PagoEntrada {
  metodo: MetodoPago;
  moneda: Moneda;
  monto: number;
  tipoCambio: number;
}

/**
 * Evalúa los pagos contra el total (en MXN). El cambio se entrega en pesos y solo puede
 * salir del efectivo recibido: una transferencia por más del total no es válida.
 */
export function resolverPago(total: number, pagos: readonly PagoEntrada[]): {
  pagadoMXN: number;
  cambioMXN: number;
  faltaMXN: number;
  valido: boolean;
} {
  const enMXN = pagos.map((p) => ({ ...p, mxn: convertirAMXN(p.monto, p.moneda, p.tipoCambio) }));
  const pagadoMXN = enMXN.reduce((s, p) => s + p.mxn, 0);
  const efectivoMXN = enMXN.filter((p) => p.metodo === 'efectivo').reduce((s, p) => s + p.mxn, 0);
  const faltaMXN = Math.max(0, total - pagadoMXN);
  const excedente = Math.max(0, pagadoMXN - total);
  const valido = pagos.every((p) => p.monto > 0) && excedente <= efectivoMXN;
  return { pagadoMXN, cambioMXN: valido ? excedente : 0, faltaMXN, valido };
}
